// 블록 ⑥ Confirmed Business (SF) | Recent Collaborations (BI) — 2열.
//
// graceful degrade(게이트A-8): SF 미연결로 confirmed가 비면 열 숨기고 recent 전체폭.
// 둘 다 비면 블록 숨김. 한쪽만 있으면 그 열이 전체폭.
import type { ReactNode } from 'react'
import { Play } from 'lucide-react'
import type { ChannelSubs, CollaborationItem, ConfirmedDeal, Platform } from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { PlatformTabs } from '../PlatformTabs'
import { AiJudgedNotice } from '../AiJudgedNotice'
import { channelEmptyText } from '../platformDefault'
import { PLATFORM_LABEL, formatCount, formatYearMonth, toWatchUrl } from './format'

interface Props {
  confirmed: ConfirmedDeal[]
  recent: CollaborationItem[]
  /** 최근 협업의 채널 탭. 확정 비즈니스(SF)는 채널과 무관해 탭을 타지 않는다. */
  platform?: Platform
  onPlatformChange?: (platform: Platform) => void
  busy?: boolean
  /** 빈 사유 판정 입력 — 계정 없음 / 수집 대상 아님 / 브랜디드 없음을 가른다. */
  channels?: ChannelSubs[]
}

// 리스트 영역 — 자연 높이 기본, 길면 max-height로 내부 스크롤(스펙 §5-E: RECENT 행 ~74px × ≥5행 ≈ 370px).
// 두 패널은 그리드 stretch로 동일 높이 정렬. 헤더(PanelTitle)는 이 영역 밖이라 고정.
// 스크롤바는 브라우저 기본(앱 공통 overflow-y-auto 패턴) — 커스텀 스타일 없음.
const PANEL_LIST = 'max-h-[370px] overflow-y-auto'

export function ConfirmedRecent({
  confirmed,
  recent,
  platform,
  onPlatformChange,
  busy,
  channels,
}: Props) {
  const hasConfirmed = confirmed.length > 0
  // 최근 협업은 채널 탭을 타므로 **비어도 패널을 접지 않는다** — 접으면 탭이 사라져
  // 다른 채널로 옮길 수단이 없어지고, 그 채널에 협업이 없다는 사실도 지워진다.
  const hasRecent = platform != null || recent.length > 0
  if (!hasConfirmed && !hasRecent) return null

  const twoCol = hasConfirmed && hasRecent

  return (
    <div
      className="mt-5 grid gap-5"
      style={{ gridTemplateColumns: twoCol ? 'minmax(0,1fr) minmax(0,1.3fr)' : 'minmax(0,1fr)' }}
    >
      {hasConfirmed && <ConfirmedPanel deals={confirmed} />}
      {hasRecent && (
        <RecentPanel
          items={recent}
          platform={platform}
          onPlatformChange={onPlatformChange}
          busy={busy}
          channels={channels}
        />
      )}
    </div>
  )
}

function PanelTitle({ children, info }: { children: string; info?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="inline-block h-[5px] w-[5px] bg-brand-blue" aria-hidden />
      <h3 className="font-display text-[16px] font-medium tracking-[0.01em] text-fg-1">
        {children}
      </h3>
      {info}
    </div>
  )
}

function ConfirmedPanel({ deals }: { deals: ConfirmedDeal[] }) {
  return (
    <div className="border border-line-hairline bg-paper p-5">
      <PanelTitle>CONFIRMED BUSINESS</PanelTitle>
      <div className={PANEL_LIST}>
        {deals.map((d, i) => (
          <div
            key={`${d.brand}-${i}`}
            className="border-b border-line-hairline py-[11px] last:border-b-0"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-display text-[14px] font-medium text-fg-1">{d.brand}</span>
              {d.status && (
                <span
                  className={[
                    'whitespace-nowrap border px-2 py-0.5 font-text text-[11px]',
                    d.active ? 'border-brand-blue text-brand-blue' : 'border-line-soft text-fg-3',
                  ].join(' ')}
                >
                  {DEAL_STATUS_EN[d.status] ?? d.status}
                </span>
              )}
            </div>
            <div className="mt-[3px] font-text text-[12px] text-fg-2">
              {[d.scope, formatYearMonth(d.date)].filter(Boolean).join(' · ')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** 진행 상태 라벨 — 화면은 영어(UI 언어 정책), BE 는 한국어 라벨을 내린다.
 *
 *  BE 의 SF 스테이지 enum(BusinessCalendarStatus)엔 영어 코드(confirmed·taxInvoiceIssued)가
 *  있지만 응답에는 한국어 status 만 실린다. 코드값을 계약에 추가하는 건 BE 변경이라,
 *  화면이 아는 값만 매핑하고 **모르는 값은 원문 그대로 둔다** — 스테이지가 늘었을 때
 *  임의로 영어를 지어내면 없는 상태를 만들어낸다.
 */
const DEAL_STATUS_EN: Record<string, string> = {
  확정: 'CONFIRMED',
  '세금계산서 발행완료': 'TAX INVOICE ISSUED',
  '논의 중': 'IN DISCUSSION',
  '계약 진행 중': 'CONTRACT IN PROGRESS',
}

function RecentPanel({
  items,
  platform,
  onPlatformChange,
  busy,
  channels,
}: {
  items: CollaborationItem[]
  platform?: Platform
  onPlatformChange?: (platform: Platform) => void
  busy?: boolean
  channels?: ChannelSubs[]
}) {
  return (
    <div className="border border-line-hairline bg-paper p-5">
      {/* 선별 기준을 ⓘ로 밝힌다. 개수는 **상한 20**이지 렌더 수가 아니다 — 렌더 수를 적으면
          4건 있는 채널에서 "4건을 보여줍니다"가 되어, 상한이 있다는 사실 자체가 사라진다.
          (BE RECENT_COLLABORATION_LIMIT = 20, 채널별로 각각 20건) */}
      <PanelTitle
        info={
          <InfoTip
            label="Recent collaborations 선별 기준"
            text="협업 콘텐츠를 최신순으로 정렬해 상위 20개를 보여줍니다."
          />
        }
      >
        RECENT COLLABORATIONS
      </PanelTitle>
      {platform && onPlatformChange && (
        <div className="mb-3">
          <PlatformTabs
            value={platform}
            onChange={onPlatformChange}
            label="최근 협업 플랫폼"
            busy={busy}
          />
        </div>
      )}
      <AiJudgedNotice className="mb-3" />
      {items.length === 0 && (
        <div className="border border-dashed border-line-soft bg-surface-soft p-6 text-center font-text text-[13px] text-fg-3">
          {channelEmptyText(platform ?? 'youtube', channels)}
        </div>
      )}
      <div className={PANEL_LIST}>
        {items.map((c, i) => {
          const inner = (
            <>
              <div className="relative h-14 w-24 flex-none overflow-hidden bg-surface-ink">
                {c.thumbnailUrl && (
                  <img
                    src={c.thumbnailUrl}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                    }}
                  />
                )}
                <span className="pointer-events-none absolute left-1 top-1 bg-black/45 px-2 py-px font-text text-[11px] text-white">
                  {PLATFORM_LABEL[c.platform]}
                </span>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/90">
                    <Play className="h-2 w-2 fill-current text-fg-1" />
                  </span>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2">
                  <span className="truncate font-display text-[14px] font-medium text-fg-1">
                    {c.brand}
                  </span>
                  {/* 0 은 "—" 로 그린다 — Instagram 이미지·캐러셀 게시물은 플랫폼이 조회수를
                      주지 않아 0 으로 내려온다(영상에만 준다). 그대로 "0"을 그리면 아무도 보지
                      않은 게시물로 읽힌다. BrandedContentCarousel 과 같은 규칙이다. */}
                  <span className="whitespace-nowrap font-text text-[12px] font-medium tabular-nums text-fg-1">
                    {c.views ? formatCount(c.views) : '—'}
                  </span>
                </div>
                <div className="truncate font-text text-[12px] text-fg-2">{c.title}</div>
              </div>
            </>
          )
          // url 있으면 행 전체를 새 탭 링크로(시각 그대로, 클릭만). 없으면 기존 비클릭.
          const cls =
            'flex items-center gap-3 border-b border-line-hairline py-[9px] last:border-b-0'
          return c.url ? (
            <a
              key={`${c.brand}-${i}`}
              href={toWatchUrl(c.url)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${cls} cursor-pointer`}
            >
              {inner}
            </a>
          ) : (
            <div key={`${c.brand}-${i}`} className={cls}>
              {inner}
            </div>
          )
        })}
      </div>
    </div>
  )
}
